<template>
  <div class="grid sm:grid-cols-2 pb-6">
    <div
      v-for="axis in AXES"
      :key="axis"
      class="px-4 max-w-xs"
      :class="axis == AXES[0] ? 'tour-mouse-config' : ''"
    >
      <h1 class="title w-full">
        <div class="tooltip px-3" :data-tip="headerTooltip(axis)">
          {{ titleCase(axis) + " speed profile" }}
        </div>

        <div class="tooltip" data-tip="Show graph">
          <PopupButton
            :placement="'auto'"
            :offset="4"
            :renderOnShow="true"
            :class="axis == AXES[0] ? 'tour-mouse-plot-btn' : ''"
            :force-show="axis == AXES[0] && help.showMousePlot"
          >
            <template #button>
              <button class="btn btn-sm btn-ghost">
                <Icon icon="mdi:chart-line" />
              </button>
            </template>
            <template #popup>
              <div
                class="w-[400px] sm:w-[600px] p-4 pt-5 pr-5"
                :class="axis == AXES[0] ? 'tour-mouse-plot' : ''"
              >
                <SpeedProfilePlot
                  :profile="kb.config.mouse[axis].profile"
                  :width="0.5"
                  :height="0.5"
                />
              </div>
            </template>
          </PopupButton>
        </div>
      </h1>

      <ToggleOption
        label="Invert direction"
        tooltip="Invert axis movement direction"
        v-model="kb.config.mouse[axis].invert"
      />

      <SettingsOption label="Divider" tooltip="Controls output speed scaling">
        <IntegerInput
          class="w-[11ch] input-sm"
          :min="0"
          :max="65535"
          v-model="kb.config.mouse[axis].profile.divider"
        />
      </SettingsOption>

      <SettingsOption
        label="Delay"
        tooltip="Delay in milliseconds from the moment key is pressed to when start speed is applied"
      >
        <div class="join w-min">
          <IntegerInput
            class="w-[9ch] input-sm join-item"
            :min="0"
            :max="65535"
            v-model="kb.config.mouse[axis].profile.delay"
          />
          <span class="join-item btn-input-unit btn-input-unit-sm">ms</span>
        </div>
      </SettingsOption>

      <SettingsOption
        label="Acceleration time"
        tooltip="Time it takes (in milliseconds) to accelerate from start speed to max speed"
      >
        <div class="join w-min">
          <IntegerInput
            class="w-[9ch] input-sm join-item"
            :min="0"
            :max="65535"
            v-model="kb.config.mouse[axis].profile.acceleration_time"
          />
          <span class="join-item btn-input-unit btn-input-unit-sm">ms</span>
        </div>
      </SettingsOption>

      <SettingsOption label="Start speed" tooltip="Initial speed value">
        <IntegerInput
          class="w-[11ch] input-sm"
          :min="0"
          :max="65535"
          v-model="kb.config.mouse[axis].profile.start_speed"
        />
      </SettingsOption>

      <SettingsOption
        label="Max speed"
        tooltip="Final speed reached after delay + acceleration time since key press"
      >
        <IntegerInput
          class="w-[11ch] input-sm"
          :min="0"
          :max="65535"
          v-model="kb.config.mouse[axis].profile.max_speed"
        />
      </SettingsOption>
    </div>
  </div>
</template>

<script setup lang="ts">
import { assertUnreachable } from "@/utils";
import { useHelpStore, useKeyboardStore } from "@/stores";
import SettingsOption from "@/components/utils/SettingsOption.vue";
import IntegerInput from "@/components/utils/IntegerInput.vue";
import PopupButton from "@/components/utils/PopupButton.vue";
import ToggleOption from "@/components/settings/ToggleOption.vue";
import SpeedProfilePlot from "@/components/mouse/SpeedProfilePlot.vue";

const kb = useKeyboardStore();
const help = useHelpStore();

const AXES = ["x", "y", "wheel", "pan"] as const;
type AxesTyple = typeof AXES;
type Axis = AxesTyple[number];

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const headerTooltip = (axis: Axis) => {
  const start = "Mouse emulation speed profile used when";
  if (axis == "x" || axis == "y")
    return `${start} moving in ${axis.toUpperCase()} direction`;
  else if (axis == "wheel") return `${start} scrolling with wheel`;
  else if (axis == "pan") return `${start} panning (scrolling horizontally)`;
  else return assertUnreachable(axis);
};
</script>

<style scoped>
.title {
  @apply block text-lg text-center mt-5 mb-1 py-1;
}
</style>
